import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFileDownloadUrl } from "../../../../api/firebase/firestoreService";
import { CmmStatus } from "../../../../enums/cmmStatus";
import { fetchCmmDiffDataForGivenOrder } from "../../../../redux/slice/cmm/cmmDiffDataSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";

const PATIENT_MISMATCH_SCREENSHOT_PATH = (identifier: string) =>
  `onco_emr/${identifier}/cmm_screenshot/CMM_Form_patient_mismatch.pdf`;

interface UseFinalScreenshotFromSliceResult {
  finalDocUrl: string | null;
  formattedTime: string;
  loading: boolean;
}

/**
 * Custom hook to fetch final screenshot from Redux cmmDiffData slice
 * Handles fetching CMM diff data, downloading screenshot URL, and formatting timestamp.
 * For CMM status "patient_mismatch", uses path: onco_emr/{identifier}/cmm_screenshot/CMM_Form_patient_mismatch.pdf
 */
export const useFinalScreenshotFromSlice = (
  orderId?: string,
  cmmStatus?: string,
): UseFinalScreenshotFromSliceResult => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    baselineData,
    currentData,
    loading: dataLoading,
  } = useSelector((state: RootState) => state.cmmDiffData);

  const [finalDocUrl, setFinalDocUrl] = useState<string | null>(null);
  const [isUrlLoading, setIsUrlLoading] = useState(false);

  // Track the current orderId to prevent race conditions
  const currentOrderIdRef = useRef<string | undefined>(orderId);

  const isPatientMismatch = cmmStatus === CmmStatus.PATIENT_MISMATCH;

  // Fetch the CMM diff data when orderId changes (skip for patient mismatch - we use fixed path)
  useEffect(() => {
    // Update the ref to track the latest orderId
    currentOrderIdRef.current = orderId;

    if (orderId) {
      // Reset state when orderId changes to avoid showing stale data
      setFinalDocUrl(null);
      setIsUrlLoading(true);
      if (!isPatientMismatch) {
        dispatch(fetchCmmDiffDataForGivenOrder(orderId));
      }
    }
  }, [orderId, dispatch, isPatientMismatch]);

  // Fetch the download URL when screenshot_path is available (or use patient mismatch path)
  useEffect(() => {
    // Capture the orderId at the time this effect runs
    const effectOrderId = currentOrderIdRef.current;
    let isCancelled = false;

    const fetchUrl = async () => {
      // For patient mismatch, use fixed path; otherwise use path from Redux
      const screenshotPath =
        isPatientMismatch && orderId
          ? PATIENT_MISMATCH_SCREENSHOT_PATH(orderId)
          : currentData?.screenshot_path || baselineData?.screenshot_path;

      if (screenshotPath) {
        setIsUrlLoading(true);
        try {
          const url = await getFileDownloadUrl(screenshotPath);
          // Only update state if this request is still relevant (orderId hasn't changed)
          if (!isCancelled && currentOrderIdRef.current === effectOrderId) {
            setFinalDocUrl(url);
          }
        } catch (error) {
          if (!isCancelled && currentOrderIdRef.current === effectOrderId) {
            console.error("Error fetching screenshot URL:", error);
            setFinalDocUrl(null);
          }
        } finally {
          if (!isCancelled && currentOrderIdRef.current === effectOrderId) {
            setIsUrlLoading(false);
          }
        }
      } else if (!dataLoading || isPatientMismatch) {
        // Data loaded but no screenshot path - not loading anymore (or patient mismatch with no orderId)
        if (!isCancelled && currentOrderIdRef.current === effectOrderId) {
          if (!isPatientMismatch || !orderId) {
            setFinalDocUrl(null);
          }
          setIsUrlLoading(false);
        }
      }
    };

    fetchUrl();

    // Cleanup function to cancel stale requests
    return () => {
      isCancelled = true;
    };
  }, [
    isPatientMismatch,
    orderId,
    currentData?.screenshot_path,
    baselineData?.screenshot_path,
    dataLoading,
  ]);

  // Use currentData timestamp, fallback to baselineData if not available (empty for patient mismatch when no slice data)
  const formattedTime =
    currentData?.created_at || baselineData?.created_at
      ? moment
          .utc(currentData?.created_at || baselineData?.created_at)
          .add(5, "hours")
          .add(30, "minutes")
          .format("MMM DD, YYYY h:mm A")
      : "";

  // Only show as not loading when both data and URL are loaded
  const isLoading = (dataLoading ?? false) || isUrlLoading;

  return {
    finalDocUrl,
    formattedTime,
    loading: isLoading,
  };
};
